import { ApiError, post } from '@/api/client';
import { destinationPath, rememberDestination } from '@/auth/destination';
import { type SessionUser, useAuth } from '@/auth/store';
import { T } from '@/i18n';
import { recordPublicPage, stopRecording, track } from '@/marketing/analytics';
import { commitGeneration } from '@/shell/context';
import { Button } from '@/ui';
import { type FormEvent, useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { SignInWith } from './konto/providers';
import { Field, KontoBox, useAvailability } from './konto/shared';

export default function Login() {
  const nav = useNavigate();
  const { search } = useLocation();
  // /login?next=demo from the site keeps the destination, as /registrer does (plan 0028 slice 1)
  useEffect(() => rememberDestination(search), [search]);
  // replay with every input masked, as on Registrer: why people stop here (#43); Shell stops it once
  // the app opens, where employee data is and nothing is recorded
  useEffect(() => recordPublicPage(), []);
  // what the server says these flows are; `undefined` until it has answered, and again if
  // the read failed, which keeps the links visible rather than claiming a door is closed
  const available = useAvailability();
  // a session is a token and a person; a cookie token alone never sends anyone back into the app
  const session = useAuth((s) => s.token != null && s.user != null);
  const interrupted = useAuth((s) => s.interrupted);
  const setSession = useAuth((s) => s.setSession);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // back from Google, Microsoft or Apple without getting in: the reason, and which provider
  const q = new URLSearchParams(search);
  const signInFailed = q.get('innlogging');
  // the index route picks the screen the role may see; /innboks is a manager screen
  if (session) return <Navigate to="/" replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    track('signin_method_chosen', { method: 'password', page: 'login' });
    setBusy(true);
    setErr(null);
    try {
      // identity: true asks for the narrow identity token when no company lists the person
      const r = await post<{ token: string; user: SessionUser; scope?: string }>('/login', {
        username: email,
        password,
        identity: true,
      });
      // the recording of this page ends before the app draws anything: its first screen holds
      // employee data (security review 2026-10-07; Shell stops it too, but only after it renders)
      stopRecording();
      track('signin_succeeded', { method: 'password' });
      setSession(r.token, r.user);
      commitGeneration(r.user.id, null, null); // a new context: nothing from a previous person is readable
      // a person with no company goes where the site sent them, their demo or a company of
      // their own, before the shell opens (TF-ACCOUNT-ACCESS; plan 0028 slice 1)
      nav(r.scope === 'identity' ? destinationPath() : '/', { replace: true });
    } catch (ex) {
      // the outcome, never the address (#102): the server's code, wrong_credentials said as the issue names it
      const code = ex instanceof ApiError ? ex.code : null;
      track('signin_failed', {
        method: 'password',
        reason: code === 'wrong_credentials' ? 'wrong_password' : (code ?? 'no_answer'),
      });
      setErr(ex instanceof ApiError ? ex.message : T('Kunne ikke logge inn'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <KontoBox title={T('Velkommen tilbake')} sub={T('Logg inn på Timefly.')}>
      {interrupted && <output className="al info">{T('Logg inn på nytt for å fortsette.')}</output>}
      {/* «Logg inn med Google/Microsoft/Apple» for an existing account (owner 2026-10-06); the provider's
          verified address finds the account. An address with no account gets a demo, never a company
          (replay cases L1 and L2, 2026-10-13) */}
      {available.signInWith.length > 0 && (
        <SignInWith providers={available.signInWith} failed={signInFailed} via={q.get('via')} login />
      )}
      <form onSubmit={submit}>
        {/* the server takes the address or, for older accounts, the username (owner, 2026-09-27).
            Sent to the password form by a provider (konto), the cursor is already in it */}
        <Field
          label={T('E-post')}
          type="text"
          inputMode="email"
          autoComplete="username"
          placeholder={T('deg@bedrift.no')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoFocus={signInFailed === 'konto'}
          required
        />
        <Field
          label={T('Passord')}
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        {/* offered only where the server says the flow is open. While the answer is being read, and
            if reading it fails, the links stay: a blink in the network is not a reason to hide a
            working door (Codex, 2026-09-16) */}
        {available.recovery !== false ? (
          <Link className="auth-right" to="/glemt-passord">
            {T('Glemt passord?')}
          </Link>
        ) : null}
        {err && <div className="al dgr">{err}</div>}
        <Button tone="pri" type="submit" disabled={busy}>
          {busy ? '…' : T('Logg inn')}
        </Button>
        {available.signup !== false ? (
          <>
            <p className="auth-alt">
              {T('Ny hos Timefly?')} <Link to="/registrer">{T('Opprett konto')}</Link>
            </p>
            {/* a demo visitor has no password; a new link is how they come back */}
            <p className="auth-alt" style={{ marginTop: -8 }}>
              {T('Har du en demo?')} <Link to="/registrer?next=demo">{T('Få en ny lenke')}</Link>
            </p>
          </>
        ) : null}
      </form>
    </KontoBox>
  );
}
